// src/components/OnboardingModal.jsx
// Shows once on first login, guides new users through the platform
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Sparkles, BarChart3, FileEdit, Target, Link2, Lightbulb } from 'lucide-react';

const STEPS = [
  {
    Icon: Sparkles,
    title: 'Welcome to ODFTrades',
    desc: 'Your personal trading performance system. ODFTrades covers your full trading workflow — from pre-session analysis to post-session review.',
    tip: null,
  },
  {
    Icon: BarChart3,
    title: 'Your Dashboard',
    desc: 'Every trade you log updates your dashboard automatically — win rate, ROI, equity curve, profit factor, discipline score. All calculated in real time.',
    tip: 'Your starting balance is set to $10,000. You can change this in Settings anytime.',
  },
  {
    Icon: FileEdit,
    title: 'Log Your First Trade',
    desc: 'Click New Trade in the sidebar. Fill in the pair, direction, entry, stop loss, and result. The pre-trade checklist keeps you disciplined before every entry.',
    tip: 'You can log completed trades or mark a trade as Open/Active and close it later from the Journal.',
  },
  {
    Icon: Target,
    title: 'Risk Manager',
    desc: 'Before every session, use the Command Center to set your daily risk limits. The position size calculator tells you the exact lot size for your risk.',
    tip: 'Set your max daily loss and max trades per day in Settings to enforce your rules.',
  },
  {
    Icon: Link2,
    title: 'Your Trade Data Analytics',
    desc: 'Every account gets a unique shareable link. Log consistently and your performance page becomes a verifiable trade data analytics you can share with anyone.',
    tip: 'You can find your shareable link in the Dashboard — click Share Dashboard.',
  },
];

const G = '#00e5a0';

export default function OnboardingModal({ onClose }) {
  const { user } = useAuth();
  const [step, setStep] = useState(0);

  const isLast = step === STEPS.length - 1;
  const current = STEPS[step];

  const finish = async () => {
    // Mark onboarding as done in profile
    if (user) {
      await supabase.from('profiles').update({ onboarded: true }).eq('id', user.id);
    }
    onClose();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:'#16161f', border:'1px solid #2a2a3e', borderRadius:20, padding:'40px 36px', width:'100%', maxWidth:460, position:'relative' }}>

        {/* Progress dots */}
        <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:28 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ width: i===step ? 20 : 6, height:6, borderRadius:3, background: i<=step ? G : '#2a2a3e', transition:'all 0.3s ease' }} />
          ))}
        </div>

        {/* Icon */}
        <div style={{ display:'flex', justifyContent:'center', marginBottom:24 }}>
          <div style={{ width:64, height:64, borderRadius:16, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', color:G, boxShadow:`0 0 24px rgba(0,229,160,0.12)` }}>
            <current.Icon size={28} strokeWidth={1.6} />
          </div>
        </div>

        {/* Content */}
        <h2 style={{ fontSize:20, fontWeight:700, color:'#f0f0f5', textAlign:'center', margin:'0 0 12px', letterSpacing:'-0.01em', fontFamily:"'IBM Plex Sans',sans-serif" }}>
          {current.title}
        </h2>
        <p style={{ fontSize:14, color:'#8888a8', lineHeight:1.8, textAlign:'center', margin:'0 0 20px', fontFamily:"'IBM Plex Sans',sans-serif" }}>
          {current.desc}
        </p>

        {current.tip && (
          <div style={{ background:'rgba(0,229,160,0.06)', border:'1px solid rgba(0,229,160,0.15)', borderRadius:10, padding:'12px 16px', marginBottom:24, display:'flex', alignItems:'flex-start', gap:10 }}>
            <Lightbulb size={14} style={{ color:G, marginTop:2, flexShrink:0 }} />
            <div style={{ fontSize:12, color:G, lineHeight:1.7, fontFamily:"'IBM Plex Sans',sans-serif" }}>
              {current.tip}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display:'flex', gap:10 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s-1)} style={{ flex:1, background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:9, padding:'12px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>
              ← Back
            </button>
          )}
          <button onClick={isLast ? finish : () => setStep(s => s+1)} style={{ flex:2, background:G, color:'#0a0a0f', border:'none', borderRadius:9, padding:'12px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", transition:'all 0.2s' }}
            onMouseEnter={e=>e.currentTarget.style.background='#00c98a'}
            onMouseLeave={e=>e.currentTarget.style.background=G}>
            {isLast ? 'Start Trading →' : 'Next →'}
          </button>
        </div>

        {/* Skip */}
        <button onClick={finish} style={{ display:'block', width:'100%', textAlign:'center', marginTop:16, background:'none', border:'none', color:'#2a2a3e', fontSize:12, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}
          onMouseEnter={e=>e.target.style.color='#55556a'}
          onMouseLeave={e=>e.target.style.color='#2a2a3e'}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
